import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import {
  GraduationCap, ArrowRight, ArrowLeft, ChevronDown, ChevronUp, BookOpen,
  Layers, PenTool, Globe, Briefcase, Mail, Clock, Printer,
} from "lucide-react";
import { TRAINING_COURSES } from "../data/training_courses";
import { getScript, getHeadshot } from "../data/training_scripts";
import LessonVideoPlayer from "../components/LessonVideoPlayer";

/* /training/:slug — renders any of the 6 platform-tool courses from the
   shared data file. Reusable accordion pattern, big senior-friendly type. */

const NAVY = "#0F172A";
const CREAM = "#FFFFFF";

const ICONS = {
  Layers, PenTool, BookOpen, Globe, Briefcase, Mail,
};

function Block({ block, accent }) {
  if (block.type === "p") {
    return <p className="text-lg leading-relaxed mb-4" style={{ color: "#0F172A" }}>{block.text}</p>;
  }
  if (block.type === "h") {
    return <h3 className="font-serif-display text-2xl mt-6 mb-3" style={{ color: NAVY, fontWeight: 700 }}>{block.text}</h3>;
  }
  if (block.type === "list") {
    return (
      <ul className="space-y-2.5 mb-5 ml-1">
        {block.items.map((it, i) => (
          <li key={i} className="text-lg leading-relaxed flex gap-3" style={{ color: "#0F172A" }}>
            <span className="font-bold flex-shrink-0 mt-0.5" style={{ color: accent }}>•</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "list-bold") {
    return (
      <ul className="space-y-3 mb-5 ml-1">
        {block.items.map(([lead, body], i) => (
          <li key={i} className="text-lg leading-relaxed" style={{ color: "#0F172A" }}>
            <strong style={{ color: accent }}>{lead}.</strong> {body}
          </li>
        ))}
      </ul>
    );
  }
  if (block.type === "callout") {
    return (
      <div className="my-5 rounded-xl p-5 border-l-4"
           style={{ background: `${accent}10`, borderColor: accent }}>
        <p className="text-lg italic leading-relaxed" style={{ color: NAVY }}>{block.text}</p>
      </div>
    );
  }
  return null;
}

export default function TrainingCoursePage({ embedded = false }) {
  const { slug } = useParams();
  const course = TRAINING_COURSES.find((c) => c.slug === slug);
  const [open, setOpen] = useState(1);

  useEffect(() => {
    if (course) {
      document.title = `${course.title} — Faitharama Academy`;
    }
  }, [course]);

  if (!course) {
    return <Navigate to="/academy" replace />;
  }

  const Icon = ICONS[course.icon] || GraduationCap;

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="training-course-page">
      {!embedded && <SiteNav />}

      {/* ═══ HERO ═══ */}
      <section className="relative overflow-hidden border-b-2"
               style={{ background: `linear-gradient(180deg, ${course.accent} 0%, ${course.accentSoft || course.accent} 100%)`,
                        borderColor: "#2E9F3A" }}>
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-12 md:py-16">
          <Link to="/academy" data-testid="training-back-link"
                className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-bold text-white/85 hover:text-white mb-5">
            <ArrowLeft className="w-3.5 h-3.5" /> All Academy Courses
          </Link>
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-3 text-white/75">
            ★ Faitharama Academy · Course {course.number}
          </div>
          <div className="flex items-start gap-4 md:gap-6">
            <div className="rounded-xl p-3 md:p-4 bg-white/15 flex-shrink-0">
              <Icon className="w-10 h-10 md:w-14 md:h-14 text-white" />
            </div>
            <div>
              <h1 className="font-serif-display text-3xl sm:text-4xl md:text-6xl uppercase leading-[0.95] text-white"
                  style={{ textWrap: "balance" }}>
                {course.title}
              </h1>
              <p className="mt-3 text-base md:text-xl text-white/90 leading-relaxed">{course.subtitle}</p>
            </div>
          </div>
          <p className="mt-6 text-base md:text-lg text-white/85 leading-relaxed max-w-3xl">{course.blurb}</p>
          <div className="mt-6 flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.18em] font-bold">
            <span className="px-3 py-1.5 rounded-md bg-white/15 text-white border border-white/25">
              <Clock className="w-3.5 h-3.5 inline mr-1.5" /> {course.minutes} min read
            </span>
            <span className="px-3 py-1.5 rounded-md bg-white/15 text-white border border-white/25">
              <BookOpen className="w-3.5 h-3.5 inline mr-1.5" /> {course.chapters.length} lessons
            </span>
            <Link to={`/academy/${course.slug}/scripts-print`}
                  data-testid="training-print-scripts-link"
                  className="px-3 py-1.5 rounded-md bg-white text-[#0F172A] hover:bg-[#FFFFFF] hover:scale-[1.03] transition shadow-sm inline-flex items-center">
              <Printer className="w-3.5 h-3.5 inline mr-1.5" /> Print Scripts (PDF)
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ TABLE OF CONTENTS + LESSONS ═══ */}
      <section className="max-w-4xl mx-auto px-5 md:px-8 py-10 md:py-14">
        <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-5" style={{ color: course.accent }}>
          ★ Course Lessons
        </div>

        <div className="space-y-3" data-testid="training-lessons">
          {course.chapters.map((ch) => {
            const isOpen = open === ch.n;
            return (
              <div key={ch.n}
                   data-testid={`training-lesson-${ch.n}`}
                   className="rounded-xl border-2 overflow-hidden transition shadow-sm"
                   style={{ background: "#fff", borderColor: isOpen ? course.accent : "#E5DDC8" }}>
                <button
                  onClick={() => setOpen(isOpen ? null : ch.n)}
                  data-testid={`training-lesson-toggle-${ch.n}`}
                  className="w-full text-left px-5 md:px-7 py-5 flex items-center justify-between gap-4 hover:bg-[#FFFFFF] transition">
                  <div className="flex items-start gap-4">
                    <span className="font-serif-display text-2xl font-bold flex-shrink-0"
                          style={{ color: course.accent, minWidth: "2.5rem" }}>
                      {String(ch.n).padStart(2, "0")}
                    </span>
                    <span className="font-serif-display text-xl md:text-2xl leading-tight"
                          style={{ color: NAVY, fontWeight: 700 }}>{ch.title}</span>
                  </div>
                  {isOpen
                    ? <ChevronUp className="w-6 h-6 flex-shrink-0" style={{ color: course.accent }} />
                    : <ChevronDown className="w-6 h-6 flex-shrink-0" style={{ color: course.accent }} />}
                </button>
                {isOpen && (
                  <div className="px-5 md:px-7 pb-7 pt-1" data-testid={`training-lesson-body-${ch.n}`}>
                    <LessonVideoPlayer
                      script={getScript(course.slug, ch.n)}
                      headshot={getHeadshot(course.slug)}
                      accent={course.accent}
                    />
                    {ch.body.map((b, i) => (
                      <Block key={i} block={b} accent={course.accent} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Next steps */}
        <div className="mt-12 rounded-2xl border-2 p-6 md:p-8 text-center"
             style={{ background: "#fff", borderColor: course.accent }}>
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold mb-2" style={{ color: course.accent }}>
            ★ When You're Ready
          </div>
          <h3 className="font-serif-display text-3xl md:text-4xl mb-3" style={{ color: NAVY, fontWeight: 700 }}>
            Try it inside your Dashboard.
          </h3>
          <p className="text-base md:text-lg max-w-2xl mx-auto mb-5 leading-relaxed" style={{ color: "#0F172A" }}>
            Reading is half the work — using the tool is the other half. Open your Dashboard and put one lesson into practice today.
          </p>
          <Link to="/dashboard" data-testid="training-cta-dashboard"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] text-white shadow-lg hover:scale-[1.03] transition"
                style={{ background: course.accent }}>
            Go To My Dashboard <ArrowRight className="w-5 h-5" />
          </Link>
        </div>

        {/* Other courses */}
        <div className="mt-14">
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-4" style={{ color: course.accent }}>
            ★ Other Academy Courses
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {TRAINING_COURSES.filter((c) => c.slug !== course.slug).slice(0, 4).map((c) => {
              const OtherIcon = ICONS[c.icon] || GraduationCap;
              return (
                <Link key={c.slug} to={`/academy/${c.slug}`}
                      data-testid={`training-other-${c.slug}`}
                      className="rounded-xl border-2 p-4 flex items-center gap-3 hover:shadow-md transition bg-white"
                      style={{ borderColor: `${c.accent}55` }}>
                  <OtherIcon className="w-7 h-7 flex-shrink-0" style={{ color: c.accent }} />
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: c.accent }}>Course {c.number}</div>
                    <div className="font-serif-display text-base leading-tight" style={{ color: NAVY, fontWeight: 700 }}>{c.title}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {!embedded && <SiteFooter />}
    </div>
  );
}
